/**
 * Broken image fallback.
 *
 * Product images occasionally fail to load — a CDN hiccup, a flaky connection, an
 * image that was removed from a product. The browser then paints its own broken image
 * glyph, which looks worse than having no image at all.
 *
 * Any image that fails is swapped for a placeholder drawn from the colour scheme it
 * sits in, so a broken image reads as a quiet empty frame instead of an error. The
 * placeholder is handed to the same `<img>` element as an inline SVG, which keeps its
 * width/height attributes, aspect ratio, `object-fit`, corner radius and hover
 * behaviour exactly as the theme styled them.
 *
 * Failures are collected from the moment the document starts parsing by the inline
 * listener in `snippets/image-fallback.liquid`, which pushes them onto
 * `window.themeBrokenImages`. This script drains that queue and then takes over.
 */
(function () {
  /** One retry before giving up — a good share of failures in the wild are transient. */
  const RETRY_DELAY = 700;

  /** Used when an image fails outside of any colour scheme. */
  const DEFAULT_BACKGROUND = '#f3f4f6';
  const DEFAULT_FOREGROUND = '#1a1d24';

  /** @type {Map<string, string>} colour pair -> data URI */
  const placeholderCache = new Map();

  /**
   * A flat panel with a centred picture glyph, drawn on a canvas that matches the
   * frame it is about to fill so that `object-fit` has nothing to crop and the glyph
   * keeps the same proportion in a tall card and a wide banner alike.
   *
   * @param {string} background
   * @param {string} foreground
   * @param {number} ratio - width / height of the frame
   */
  function buildPlaceholder(background, foreground, ratio) {
    const key = background + '|' + foreground + '|' + ratio;
    const cached = placeholderCache.get(key);
    if (cached) return cached;

    // The short side is always 100 units, so the glyph is sized against it.
    const width = ratio >= 1 ? 100 * ratio : 100;
    const height = ratio >= 1 ? 100 : 100 / ratio;
    const offsetX = width / 2 - 50;
    const offsetY = height / 2 - 50;

    const svg = [
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + width + ' ' + height + '" ',
      'preserveAspectRatio="xMidYMid slice">',
      '<rect width="100%" height="100%" fill="' + background + '"/>',
      '<rect width="100%" height="100%" fill="' + foreground + '" opacity="0.05"/>',
      '<g transform="translate(' + offsetX + ' ' + offsetY + ') ',
      'translate(50 50) scale(0.82) translate(-50 -50)" ',
      'fill="none" stroke="' + foreground + '" stroke-opacity="0.32" stroke-width="2.4" ',
      'stroke-linecap="round" stroke-linejoin="round">',
      '<rect x="31" y="35" width="38" height="30" rx="3.5"/>',
      '<circle cx="42" cy="45.5" r="3.4"/>',
      '<path d="M32.2 60.4 41.9 52a3 3 0 0 1 3.9 0l6 5.2 4.4-3.6a3 3 0 0 1 3.8 0l7.8 6.4"/>',
      '</g>',
      '</svg>',
    ].join('');

    const uri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    placeholderCache.set(key, uri);
    return uri;
  }

  /**
   * The proportions the placeholder has to take on.
   *
   * Once the placeholder SVG is in place it carries its own intrinsic ratio, and a
   * frame sized with `height: auto` will follow it — so getting this wrong visibly
   * distorts the layout. In order of trust:
   *
   *   1. an `aspect-ratio` the theme put on the frame, which is what the image was
   *      being cropped to anyway;
   *   2. the `width`/`height` attributes `image_tag` emits, which carry the real
   *      proportions of the original image;
   *   3. the rendered box — last, because a frame that takes its height from the
   *      image has already collapsed by the time we look at it. Only a plausible
   *      box is trusted; anything sliver-shaped is that collapse, not a design, and
   *      a square is the safer guess.
   *
   * @param {HTMLImageElement} img
   * @param {CSSStyleDeclaration} styles
   */
  function frameRatio(img, styles) {
    const declared = ratioFromAspectRatio(styles.aspectRatio);
    if (declared) return declared;

    const attributes = toRatio(
      parseFloat(img.getAttribute('width') || ''),
      parseFloat(img.getAttribute('height') || '')
    );
    if (attributes) return attributes;

    const rect = img.getBoundingClientRect();
    if (rect.width > 8 && rect.height > 8) {
      const rendered = toRatio(rect.width, rect.height);
      if (rendered >= 0.4 && rendered <= 2.5) return rendered;
    }

    return 1;
  }

  /**
   * Parses a computed `aspect-ratio`, which is `auto` when unset and otherwise a
   * number or a `4 / 5` pair.
   *
   * @param {string} value
   */
  function ratioFromAspectRatio(value) {
    if (!value || value === 'auto') return 0;

    const parts = value.split('/');
    const width = parseFloat(parts[0]);
    const height = parts.length > 1 ? parseFloat(parts[1]) : 1;
    return toRatio(width, height);
  }

  /**
   * @param {number} width
   * @param {number} height
   * @returns {number} 0 when the pair is unusable
   */
  function toRatio(width, height) {
    if (!(width > 0) || !(height > 0)) return 0;
    // Rounded so a grid of near-identical cards shares one cached placeholder.
    return Math.round(Math.min(Math.max(width / height, 0.2), 5) * 20) / 20;
  }

  /**
   * Custom properties are read as authored, so only let a plain colour value through
   * before it is written into an SVG attribute.
   *
   * @param {string} value
   * @param {string} fallback
   */
  function colorOrFallback(value, fallback) {
    const trimmed = (value || '').trim();
    return /^[a-zA-Z0-9#%.,/()\s]+$/.test(trimmed) ? trimmed : fallback;
  }

  /**
   * Reads the colours of whichever colour scheme the image sits in, so the
   * placeholder matches a light card and a dark section alike.
   *
   * @param {HTMLImageElement} img
   */
  function placeholderFor(img) {
    const styles = getComputedStyle(img);
    const background = colorOrFallback(
      styles.getPropertyValue('--color-background'),
      DEFAULT_BACKGROUND
    );
    const foreground = colorOrFallback(
      styles.getPropertyValue('--color-foreground'),
      DEFAULT_FOREGROUND
    );

    return buildPlaceholder(background, foreground, frameRatio(img, styles));
  }

  /**
   * True when the browser has finished with the image and ended up with nothing to
   * paint. Images that never had a source are ignored — the theme renders its own
   * Liquid placeholders for those.
   *
   * @param {HTMLImageElement} img
   */
  function hasFailed(img) {
    if (!img.complete || img.naturalWidth > 0) return false;
    return Boolean(img.currentSrc || img.getAttribute('src') || img.getAttribute('srcset'));
  }

  /** @param {HTMLImageElement} img */
  function scheduleRetry(img) {
    const src = img.getAttribute('src');
    const srcset = img.getAttribute('srcset');

    if (!src && !srcset) {
      applyPlaceholder(img);
      return;
    }

    img.dataset.imageFallback = 'retrying';

    setTimeout(function () {
      img.dataset.imageFallback = 'retried';
      // Re-setting the attribute restarts the image request even when the value is
      // unchanged, so the responsive sources are kept as they were.
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
    }, RETRY_DELAY);
  }

  /** @param {HTMLImageElement} img */
  function applyPlaceholder(img) {
    img.dataset.imageFallback = 'applied';
    // The responsive sources have to go, otherwise the browser keeps picking a
    // candidate from `srcset` and re-runs the request that just failed.
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.removeAttribute('data-max-resolution');
    img.src = placeholderFor(img);
  }

  /** @param {EventTarget | null} target */
  function handle(target) {
    if (!(target instanceof HTMLImageElement)) return;

    const state = target.dataset.imageFallback;
    if (state === 'applied' || state === 'retrying') return;
    if (state === 'retried') {
      applyPlaceholder(target);
      return;
    }

    scheduleRetry(target);
  }

  /**
   * Catches images that failed before this script ran, images restored from the
   * back/forward cache, and images whose failed request was already cached.
   *
   * @param {ParentNode} root
   */
  function sweep(root) {
    root.querySelectorAll('img').forEach(function (img) {
      if (hasFailed(img)) handle(img);
    });
  }

  const queued = window.themeBrokenImages || [];
  queued.forEach(handle);
  queued.length = 0;
  // The inline listener stays registered, so hand anything it reports from here on
  // straight to the handler.
  queued.push = handle;

  // Kept alongside the inline listener so the fallback still works if the inline
  // snippet is ever dropped. A double call is harmless — `handle` is guarded by state.
  window.addEventListener(
    'error',
    function (event) {
      handle(event.target);
    },
    true
  );

  sweep(document);
  window.addEventListener('load', function () {
    sweep(document);
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) sweep(document);
  });

  // Search results, filtered collection grids and quick-add drawers all inject markup
  // after load. Their images raise their own error events once connected; this only
  // has to catch the ones that arrive already failed.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node instanceof HTMLImageElement) {
          if (hasFailed(node)) handle(node);
        } else if (node instanceof Element) {
          sweep(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
